import { useState, useEffect, useRef, useMemo } from 'react';
import scribe from '@grab-x/scribe-web-sdk';
import { GRABX_OVERRIDES_KEY } from 'src/constants/localStorage';
import { isProdEnv } from '@utils/index';

// Global cache for GrabX variables
type CacheEntry<T = any> = {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  promise?: Promise<void>;
};

export const getGrabXOverrides = (): Record<string, boolean> => {
  if (isProdEnv()) return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(GRABX_OVERRIDES_KEY) ?? '{}');
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
    return {};
  } catch {
    return {};
  }
};

export const setGrabXOverride = (key: string, value: boolean) => {
  const overrides = getGrabXOverrides();
  overrides[key] = value;
  localStorage.setItem(GRABX_OVERRIDES_KEY, JSON.stringify(overrides));
};

export const clearGrabXOverride = (key: string) => {
  const overrides = getGrabXOverrides();
  delete overrides[key];
  localStorage.setItem(GRABX_OVERRIDES_KEY, JSON.stringify(overrides));
};

export const clearAllGrabXOverrides = () => {
  localStorage.removeItem(GRABX_OVERRIDES_KEY);
};

const globalCache = new Map<string, CacheEntry>();

// Utility function to clear cache for a specific variable or all variables
export const clearGrabXCache = (variableName?: string) => {
  if (variableName) {
    globalCache.delete(variableName);
  } else {
    globalCache.clear();
  }
};

const useGrabXVariable = <T = any>(variableName: string, facets: Record<string, any> = {}, enabled: boolean = true) => {
  const overrideKey = facets.fea ? `${variableName}:${facets.fea}` : variableName;
  const overrides = getGrabXOverrides();
  const overrideValue = overrides[overrideKey];
  const hasOverride = overrideKey in overrides;

  // Create a stable cache key that includes facets
  const cacheKey = useMemo(() => {
    const facetsString = JSON.stringify(facets);
    return `${variableName}:${facetsString}`;
  }, [variableName, facets]);

  const [data, setData] = useState<T | null>(hasOverride ? (overrideValue as unknown as T) : null);
  const [isLoading, setIsLoading] = useState(!hasOverride);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (hasOverride) {
      setData(overrideValue as unknown as T);
      setIsLoading(false);
      return () => {
        isMountedRef.current = false;
      };
    }

    if (!enabled) {
      setIsLoading(false);
      return () => {
        isMountedRef.current = false;
      };
    }

    // Reset mounted flag when effect runs
    isMountedRef.current = true;

    const fetchVariable = async () => {
      // Check if we already have this variable in cache
      const cached = globalCache.get(cacheKey);

      if (cached) {
        // If there's an ongoing request, wait for it
        if (cached.isLoading && cached.promise) {
          try {
            await cached.promise;
            if (!isMountedRef.current) return;

            const updatedCache = globalCache.get(cacheKey);
            if (updatedCache) {
              setData(updatedCache.data);
              setError(updatedCache.error);
              setIsLoading(false);
            }
          } catch {
            if (!isMountedRef.current) return;

            const updatedCache = globalCache.get(cacheKey);
            if (updatedCache) {
              setData(updatedCache.data);
              setError(updatedCache.error);
              setIsLoading(false);
            }
          }
          return;
        }

        // If we have cached data (success or error), use it immediately
        if (!cached.isLoading) {
          if (!isMountedRef.current) return;
          setData(cached.data);
          setError(cached.error);
          setIsLoading(false);
          return;
        }
      }

      // Create cache entry for ongoing request
      const cacheEntry: CacheEntry<T> = {
        data: null,
        error: null,
        isLoading: true,
      };

      const fetchPromise = (async () => {
        try {
          console.log(`Fetching GrabX variable: ${variableName} with facets:`, facets);

          const result = await scribe.getVariable<string>(variableName, facets);
          const { value } = result;

          let parsedData: T | null = null;
          if (value !== undefined && value !== null) {
            if (typeof value === 'string') {
              try {
                parsedData = JSON.parse(value);
              } catch {
                // If parsing fails, use raw value
                parsedData = value as unknown as T;
              }
            } else {
              parsedData = value as unknown as T;
            }
          }

          // Update cache with success
          cacheEntry.data = parsedData;
          cacheEntry.error = null;
          cacheEntry.isLoading = false;
          delete cacheEntry.promise;

          // Update component state only if still mounted
          if (isMountedRef.current) {
            setData(parsedData);
            setError(null);
            setIsLoading(false);
          }
        } catch (err) {
          const error = err instanceof Error ? err : new Error(`Failed to fetch GrabX variable: ${String(err)}`);
          console.error(`Failed to fetch GrabX variable "${variableName}":`, error);

          // Update cache with error
          cacheEntry.data = null;
          cacheEntry.error = error;
          cacheEntry.isLoading = false;
          delete cacheEntry.promise;

          // Update component state only if still mounted
          if (isMountedRef.current) {
            setData(null);
            setError(error);
            setIsLoading(false);
          }
        }
      })();

      cacheEntry.promise = fetchPromise;
      globalCache.set(cacheKey, cacheEntry);

      // Set initial loading state
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }
    };

    fetchVariable().catch((err) => {
      console.error('Unhandled error in fetchVariable:', err);
    });

    // Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [cacheKey, variableName, facets, enabled, hasOverride, overrideValue]);

  return {
    data,
    isLoading,
    error,
    isReady: !isLoading,
  };
};

export default useGrabXVariable;
